import { useState, useEffect } from 'react';
import { shouldSectionBeExpanded, NavSection, NavConfigItem, NavNestedSection, isNavNestedSection } from '../index';

/**
 * Return set of section keys to be expanded based on the active route
 * Key format: [sectionIndex]-[nestedIndex]
 * @returns expandedSections `Set<string>`
 */
const getInitialExpandedSections = (pathname: string, navSections: NavConfigItem[]): Set<string> => {
  const expandedSections = new Set<string>();

  navSections.forEach((item, index) => {
    // Skip dividers and nav items, only process sections
    if ('type' in item || 'label' in item) {
      return;
    }

    const section = item as NavSection;

    // Check NavSection (1 layer deep)
    if (shouldSectionBeExpanded(pathname, section)) {
      expandedSections.add(String(index));
    }

    // Check nested sections (2 layers deep)
    section.children?.forEach((child, childIndex) => {
      if (isNavNestedSection(child)) {
        const nestedSection = child as NavNestedSection;
        if (shouldSectionBeExpanded(pathname, nestedSection)) {
          // Expand parent (if not already added) and child sections
          expandedSections.add(String(index));
          expandedSections.add(`${index}-${childIndex}`);
        }
      }
    });
  });

  return expandedSections;
};

/**
 * Hook to manage which navigation sections are expanded.
 * Expands sections containing the active route on initialization.
 */
export const useExpandedNavSections = (pathname: string, navSections: NavConfigItem[]) => {
  const [openSections, setOpenSections] = useState<Set<string>>(() =>
    getInitialExpandedSections(pathname, navSections),
  );

  useEffect(() => {
    setOpenSections(getInitialExpandedSections(pathname, navSections));
  }, [pathname, navSections]);

  const toggleSection = (key: string) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return { openSections, toggleSection };
};
